/**
 * Global API Response Handler
 * Provides centralized error handling for axios responses and thunk API rejections
 */

import { handleGlobalError } from './errorHandler';
import { APIResponseError } from './errors';

/**
 * Extracts error message from axios error response
 * @param {Object} error - Axios error object
 * @returns {string} - Extracted error message
 */
export const extractErrorMessage = (error) => {
  // Handle axios error response structure
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Handle other axios error structures
  if (error?.response?.data && typeof error.response.data === 'string') {
    return error.response.data;
  }
  
  // Handle general axios error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback to string representation
  return error.toString();
};

/**
 * Global handler for Redux async thunk errors
 * @param {Object} error - Error object from axios
 * @param {Object} thunkAPI - Redux thunk API object
 * @param {string} context - Context information (e.g., thunk name)
 * @returns {Object} - Rejected value for thunk
 */
export const handleThunkError = (error, thunkAPI, context = 'Redux Thunk') => {
  const message = extractErrorMessage(error);
  
  // Create API response error for proper error handling
  const apiError = new APIResponseError(message, {
    statusCode: error?.response?.status,
    originalError: error,
    context
  });
  
  // Log error through global error handler
  handleGlobalError(apiError, context);
  
  // Return rejected value for Redux
  return thunkAPI.rejectWithValue(message);
};

/**
 * Global handler for general API errors
 * @param {Object} error - Error object from axios
 * @param {string} context - Context information
 * @throws {APIResponseError} - Throws formatted API error
 */
export const handleAPIError = (error, context = 'API Call') => {
  const message = extractErrorMessage(error);
  
  const apiError = new APIResponseError(message, {
    statusCode: error?.response?.status,
    originalError: error,
    context
  });
  
  // Log error through global error handler
  handleGlobalError(apiError, context);
  
  // Throw error for caller to handle
  throw apiError;
};

/**
 * Creates a standardized try-catch wrapper for API calls
 * @param {Function} apiCall - The API call function
 * @param {string} context - Context information
 * @returns {Function} - Wrapped API call with error handling
 */
export const withErrorHandling = (apiCall, context) => {
  return async (...args) => {
    try {
      return await apiCall(...args);
    } catch (error) {
      handleAPIError(error, context);
    }
  };
};

/**
 * Creates a standardized try-catch wrapper for Redux thunks
 * @param {Function} thunkFunction - The thunk function
 * @param {string} context - Context information
 * @returns {Function} - Wrapped thunk with error handling
 */
export const withThunkErrorHandling = (thunkFunction, context) => {
  return async (payload, thunkAPI) => {
    try {
      return await thunkFunction(payload, thunkAPI);
    } catch (error) {
      return handleThunkError(error, thunkAPI, context);
    }
  };
};

/**
 * Axios response interceptor setup for global error handling
 * Call this function once in your main App component or index file
 */
export const setupGlobalResponseInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => {
      // Return successful responses as-is
      return response;
    },
    (error) => {
      // Create context from request config
      const context = `${error?.config?.method?.toUpperCase() || 'UNKNOWN'} ${error?.config?.url || 'unknown-endpoint'}`;
      
      // Log error through global error handler
      const apiError = new APIResponseError(extractErrorMessage(error), {
        statusCode: error?.response?.status,
        originalError: error,
        context
      });
      
      handleGlobalError(apiError, context);
      
      // Return rejected promise to maintain axios behavior
      return Promise.reject(error);
    }
  );
};
